// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';

import { useCustomerConfig, useRooftop, useUser } from '@store';
import { Dealer_Viewable_Driver_Info, Moves } from '@gql/base-types';
import {
  MoveDetailsEditModals,
  canChangeMoveLocation,
  useMoveDetailsEdits,
} from '@hopdrive/feature-move-planner';
import { findOffersInScenarios } from '@services/moveService';
import { isMoveLite } from '@utils/tiers';

import { css } from '@emotion/css';
import { Theme, useTheme, Paper, Typography } from '@mui/material';

import { Divider } from '@hopdrive/sdk-ui';
import DriverTracker from '@components/DriverTracker/DriverTracker';
import MoveStatusTracker from '@components/MoveStatusTracker';
import Spacer from '@components/Utils/Spacer';

import MoveDetailsToolbar from '@features/moves/components/MoveDetailsToolbar';
import MoveDetailsOffer from '@features/moves/components/MoveDetailsOffer';
import MoveDetailsInfo from '@features/moves/components/MoveDetailsInfo';
import MoveDetailsEmailNotifications from '@features/moves/components/MoveDetailsEmailNotifications';
import MoveDetailsTimeline from '@features/moves/components/MoveDetailsTimeline';
import MoveDetailsImages from '@features/moves/components/MoveDetailsImages';
import MoveDetailsRelated from '@features/moves/components/MoveDetailsRelated';
import MoveDetailsRide from '@features/moves/components/MoveDetailsRide';
import ConfirmationBanner from '@features/moves/components/ConfirmationBanner';
import LocationConfirmationCard from '@features/moves/components/LocationConfirmationCard';

import config from '@config';
const {
  ridehail: { enabled },
} = config;

// HELPERS ---------------------------------------------------------------- //

const getRelatedMoves = (move?: Moves) => {
  const relatedMoves = [];

  const childMoves = move?.childMoves?.filter(cm => cm?.move_type === `drive`);
  if (childMoves?.length) childMoves?.forEach(cm => relatedMoves.push(cm));

  const parentMove = move?.parentMove;
  if (parentMove) relatedMoves.push(parentMove);

  return relatedMoves;
};

// TYPES ---------------------------------------------------------------- //

interface MoveDetailsContentProps {
  move?: Moves;
  driver?: Dealer_Viewable_Driver_Info;
}

// COMPONENT ---------------------------------------------------------------- //

const MoveDetailsContent = ({ move, driver }: MoveDetailsContentProps) => {
  const theme = useTheme();
  const cls = useStyles(theme);

  const user = useUser();
  const rooftop = useRooftop();
  const customerConfig = useCustomerConfig();
  const relatedMoves = getRelatedMoves(move);
  const offers = findOffersInScenarios(move?.scenariotomoves || []);

  // A pickup or delivery changes while the move has no status (no driver dispatched)
  const canChangeLocation = canChangeMoveLocation(move);
  const updatedBy = user?.profile?.email || `dealer`;

  // Edit mode, its save and audit event, and the vehicle and odometer modals
  const edits = useMoveDetailsEdits({ move, updatedBy });

  // Lite State
  const [isAssigningOrUnassigning, setIsAssigningOrUnassigning] = React.useState<boolean>(false);
  // Ref to hold the assign modal opener - avoids infinite loops from state-based callbacks
  const openAssignModalRef = React.useRef<(() => void) | null>(null);

  // History expanded state (shared between pickup and delivery cards)
  const [historyExpanded, setHistoryExpanded] = React.useState<boolean>(false);

  // Render the move details page
  return (
    <div className={cls.moveDetails}>
      {/* MOVE TOOLBAR */}
      <MoveDetailsToolbar
        move={move}
        offers={offers}
        isEditing={edits.isEditing}
        onEdit={edits.setIsEditing}
        onSave={edits.save}
        onDiscard={edits.discard}
        isAssigningOrUnassigning={isAssigningOrUnassigning}
        onAssignOrUnassign={setIsAssigningOrUnassigning}
        onOpenAssignModal={opener => { openAssignModalRef.current = opener; }}
      />

      {/* NO MOVE FOUND */}
      {!move ? (
        <Paper variant='custom' sx={{ padding: `16px`, textAlign: `center` }}>
          <Typography
            variant='h5'
            sx={{
              fontWeight: 600,
              color: theme.palette.text.disabled,
            }}
          >
            NO MOVE RECORD FOUND
          </Typography>
        </Paper>
      ) : null}

      {/* MOVE FOUND */}
      {move ? (
        <>
          {/* CONFIRMATION BANNER - Moved to top */}
          <ConfirmationBanner move={move} />

          {/* MOVE OFFER */}
          <MoveDetailsOffer
            move={move}
            offers={offers}
            isAssigningOrUnassigning={isAssigningOrUnassigning}
            onAssignOrUnassign={() => openAssignModalRef.current?.()}
          />

          {/* MOVE STATUS */}
          <div style={{ marginBottom: `24px` }}>
            <MoveStatusTracker move={move} size='lg' />
          </div>

          {/* MOVE INFO */}
          <MoveDetailsInfo
            move={move}
            driver={driver}
            isEditing={edits.isEditing}
            hasDriver={!!move?.driver_id}
            editContact={edits.edits.contact}
            editNotes={edits.edits.notes}
            editReferenceNum={edits.edits.referenceNum}
            editVin={edits.edits.vin}
            editStock={edits.edits.stock}
            setEditContact={edits.setContact}
            setEditNotes={edits.setNotes}
            setEditReferenceNum={edits.setReferenceNum}
            setEditVin={edits.setVin}
            setEditStock={edits.setStock}
            editConsumerName={edits.edits.consumerName}
            editConsumerPhone={edits.edits.consumerPhone}
            setEditConsumerName={edits.setConsumerName}
            setEditConsumerPhone={edits.setConsumerPhone}
            onEditVehicle={edits.vehicleModal.show}
            onEditOdometer={edits.odometerModal.show}
          />

          {/* EDIT VEHICLE AND ODOMETER MODALS */}
          <MoveDetailsEditModals edits={edits} />

          {/* RIDE INFORMATION */}
          {/* No ride.lite check: backend-dispatched rides carry lite=false even
              on lite parents, and isMoveLite(move) already scopes this section. */}
          {enabled &&
          isMoveLite(move) &&
          move?.childMoves?.some(
            childMove => childMove?.move_type === 'ride' && childMove?.active
          ) ? (
            <>
              <Divider spacer mb={6} tip='View ride information including provider and status.'>
                Ride Information
              </Divider>

              <MoveDetailsRide move={move} />
            </>
          ) : null}

          {/* EMAIL NOTIFICATIONS */}
          {customerConfig?.allow_email_notifications ? (
            <>
              <Divider
                spacer
                mb={6}
                tip={`View the email addresses that will receive pickup and delivery notifications for this move.`}
              >
                Email Notifications
              </Divider>

              <MoveDetailsEmailNotifications
                move={move}
                isEditing={edits.isEditing}
                hasDriver={!!move?.driver_id}
                editConfig={edits.edits.config}
                setEditConfig={edits.setConfig}
              />
            </>
          ) : null}

          {/* LANE & LOCATIONS */}
          <Divider spacer mb={6} tip='View the pickup & delivery this move is associated with.'>
            Pickup & Delivery
          </Divider>

          <MoveDetailsTimeline move={move} />

          <Spacer size={1} />

          {/* Location cards - Edit mode is now handled within each card */}
          <div className={cls.flex}>
            <LocationConfirmationCard
              move={move}
              type='pickup'
              historyExpanded={historyExpanded}
              onHistoryToggle={() => setHistoryExpanded(!historyExpanded)}
              canEdit={canChangeLocation}
              rooftopId={rooftop?.id}
              updatedBy={updatedBy}
            />
            <LocationConfirmationCard
              move={move}
              type='delivery'
              historyExpanded={historyExpanded}
              onHistoryToggle={() => setHistoryExpanded(!historyExpanded)}
              canEdit={canChangeLocation}
              rooftopId={rooftop?.id}
              updatedBy={updatedBy}
            />
          </div>

          {/* TRACKING */}
          {!move?.cancel_status && (move?.status?.includes(`pickup`) || move?.status?.includes(`delivery`)) ? (
            <>
              <Divider spacer mb={6} tip='Track where the driver has been.'>
                Tracking
              </Divider>

              <DriverTracker move={move} />
            </>
          ) : null}

          {/* IMAGES */}
          {move?.pickup_photos?.length || move?.delivery_photos?.length ? (
            <>
              <Divider spacer mb={6} tip='View images from the pickup and delivery.'>
                Images
              </Divider>

              <MoveDetailsImages move={move} />
            </>
          ) : null}

          {/* RELATED MOVES */}
          {relatedMoves?.length ? (
            <>
              <Divider spacer mb={6} tip='View any moves related to this move.'>
                Related Moves
              </Divider>

              <MoveDetailsRelated move={move} relatedMoves={relatedMoves} />
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (theme?: Theme) => ({
  moveDetails: css`
    position: relative;
  `,

  flex: css`
    display: flex;
    gap: 16px;
    ${theme?.breakpoints?.down(`md`)} {
      flex-direction: column;
    }
  `,
});

// EXPORT ---------------------------------------------------------------- //

export default MoveDetailsContent;
